import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Divider,
  Paper,
  Alert,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  GitHub as GitHubIcon,
  Language as WebsiteIcon,
  Work as WorkIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { ProfileDto, UpdateProfileRequestDto, CreateSocialLinkDto } from '../../services/socialService';
import socialService from '../../services/socialService';
import RichTextEditor from '../shared/RichTextEditor';
import { uploadInlineImage } from '../../utils/uploadInlineImage';
import { ProfileCoverBanner, ProfileMediaAvatar } from './ProfileMediaImage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface ProfileEditorProps {
  profile: ProfileDto;
  onSave: () => void;
  onCancel: () => void;
  /** Reload profile from API without closing the editor (interests, social links). */
  onProfileRefresh?: () => Promise<void>;
  /** Tab to show when opening (0=Personal, 1=Professional, 2=Social Media, 3=Interests). */
  initialTab?: number;
  /** Called when user switches tab so parent can restore this tab when returning to view. */
  onTabChange?: (tab: number) => void;
}

const EnhancedProfileEditor: React.FC<ProfileEditorProps> = ({
  profile,
  onSave,
  onCancel,
  onProfileRefresh,
  initialTab = 0,
  onTabChange,
}) => {
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<UpdateProfileRequestDto>({
    chrDisplayName: profile.chrDisplayName || profile.chrAliasName || '',
    txtBio: profile.txtBio || '',
    chrProfileImageURL: profile.chrProfileImageURL || '',
    chrCoverImageURL: profile.chrCoverImageURL || '',
    chrLocation: profile.chrLocation || '',
    chrWebsiteURL: profile.chrWebsiteURL || '',
    intVisibilityLevel: profile.intVisibilityLevel,
    chrJobTitle: profile.chrJobTitle || '',
    chrCompany: profile.chrCompany || '',
    txtSkills: profile.txtSkills || '',
    chrEducation: profile.chrEducation || '',
    txtProfessionalBio: profile.txtProfessionalBio || ''
  });

  // Social links state (kept for local updates)
  const [_socialLinks, _setSocialLinks] = useState(profile.socialLinks || []);
  const [newSocialLink, setNewSocialLink] = useState<CreateSocialLinkDto>({
    chrPlatform: 'LinkedIn',
    chrPlatformURL: '',
    chrPlatformUsername: ''
  });

  // Interests state (optimistic updates after add/delete API calls)
  const [interests, setInterests] = useState(profile.interests ?? []);
  const [newInterest, setNewInterest] = useState('');
  const [interestError, setInterestError] = useState<string | null>(null);

  useEffect(() => {
    setInterests(profile.interests ?? []);
  }, [profile.idProfileID, profile.interests]);
  const [skillInput, setSkillInput] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [mediaUploadMessage, setMediaUploadMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const skillsList = (formData.txtSkills ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const addSkill = () => {
    const next = skillInput.trim();
    if (!next) return;
    const merged = [...skillsList, next]
      .filter((s, i, arr) => arr.findIndex((x) => x.toLowerCase() === s.toLowerCase()) === i);
    setFormData({ ...formData, txtSkills: merged.join(', ') });
    setSkillInput('');
  };

  const removeSkill = (skill: string) => {
    const merged = skillsList.filter((s) => s.toLowerCase() !== skill.toLowerCase());
    setFormData({ ...formData, txtSkills: merged.join(', ') });
  };

  const handleMediaUpload = async (kind: 'avatar' | 'cover', file: File) => {
    const setUploading = kind === 'avatar' ? setUploadingAvatar : setUploadingCover;
    const field = kind === 'avatar' ? 'chrProfileImageURL' : 'chrCoverImageURL';
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, [field]: previewUrl }));
    setMediaUploadMessage(null);
    setUploading(true);
    try {
      const url = await socialService.uploadProfileMedia(profile.idAliasID, kind, file);
      URL.revokeObjectURL(previewUrl);
      setFormData((prev) => ({ ...prev, [field]: url }));
      setMediaUploadMessage({
        severity: 'success',
        text: kind === 'avatar' ? 'Profile picture uploaded.' : 'Cover photo uploaded.',
      });
    } catch (error) {
      console.error(`Failed to upload ${kind}:`, error);
      URL.revokeObjectURL(previewUrl);
      setFormData((prev) => ({
        ...prev,
        [field]: kind === 'avatar' ? (profile.chrProfileImageURL || '') : (profile.chrCoverImageURL || ''),
      }));
      setMediaUploadMessage({
        severity: 'error',
        text: error instanceof Error ? error.message : `Failed to upload ${kind} image.`,
      });
    } finally {
      setUploading(false);
    }
  };

  const uploadImageForEditor = useCallback((file: File) => uploadInlineImage(file), []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    onTabChange?.(newValue);
  };

  const handleChange = (field: keyof UpdateProfileRequestDto) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Send explicit payload so all fields (including professional) are always included
      const payload: UpdateProfileRequestDto = {
        chrDisplayName: formData.chrDisplayName ?? profile.chrAliasName ?? '',
        txtBio: formData.txtBio ?? '',
        chrLocation: formData.chrLocation ?? '',
        chrWebsiteURL: formData.chrWebsiteURL ?? '',
        intVisibilityLevel: formData.intVisibilityLevel ?? 1,
        chrJobTitle: formData.chrJobTitle ?? '',
        chrCompany: formData.chrCompany ?? '',
        txtSkills: formData.txtSkills ?? '',
        chrEducation: formData.chrEducation ?? '',
        txtProfessionalBio: formData.txtProfessionalBio ?? ''
      };
      await socialService.updateProfile(profile.idAliasID, payload);
      onSave();
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const refreshProfileData = async () => {
    if (onProfileRefresh) {
      await onProfileRefresh();
    }
  };

  const handleAddSocialLink = async () => {
    if (!newSocialLink.chrPlatformURL) {
      alert('Please enter a URL');
      return;
    }

    try {
      await socialService.addSocialLink(profile.idProfileID, newSocialLink);
      setNewSocialLink({ chrPlatform: 'LinkedIn', chrPlatformURL: '', chrPlatformUsername: '' });
      await refreshProfileData();
    } catch (error) {
      console.error('Failed to add social link:', error);
      alert('Failed to add social link');
    }
  };

  const handleDeleteSocialLink = async (linkId: string) => {
    if (!confirm('Delete this social link?')) return;
    
    try {
      await socialService.deleteSocialLink(profile.idProfileID, linkId);
      await refreshProfileData();
    } catch (error) {
      console.error('Failed to delete social link:', error);
    }
  };

  const handleAddInterest = async () => {
    if (!newInterest.trim()) return;
    if (!profile.idProfileID) {
      setInterestError('Save your profile first, then add interests.');
      return;
    }

    setInterestError(null);
    try {
      const added = await socialService.addInterest(profile.idProfileID, {
        chrInterestTag: newInterest.trim(),
      });
      setNewInterest('');
      setInterests((prev) => {
        if (prev.some((i) => i.idInterestID === added.idInterestID)) return prev;
        return [...prev, added].sort((a, b) =>
          a.chrInterestTag.localeCompare(b.chrInterestTag, undefined, { sensitivity: 'base' }));
      });
      await refreshProfileData();
    } catch (error: unknown) {
      console.error('Failed to add interest:', error);
      const message = error instanceof Error ? error.message : 'Failed to add interest';
      setInterestError(message);
      await refreshProfileData();
    }
  };

  const handleDeleteInterest = async (interestId: string) => {
    try {
      await socialService.deleteInterest(profile.idProfileID, interestId);
      setInterests((prev) => prev.filter((i) => i.idInterestID !== interestId));
      await refreshProfileData();
    } catch (error) {
      console.error('Failed to delete interest:', error);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'linkedin': return <LinkedInIcon />;
      case 'facebook': return <FacebookIcon />;
      case 'twitter': return <TwitterIcon />;
      case 'instagram': return <InstagramIcon />;
      case 'youtube': return <YouTubeIcon />;
      case 'github': return <GitHubIcon />;
      default: return <WebsiteIcon />;
    }
  };

  return (
    <Card elevation={3}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ProfileMediaAvatar
            mediaUrl={formData.chrProfileImageURL}
            sx={{ width: 80, height: 80, mr: 2 }}
          />
          <Box>
            <Typography variant="h5">
              Edit Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {profile.chrDisplayName || profile.chrAliasName} (@{profile.chrAliasName})
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab icon={<PersonIcon />} label="Personal" />
            <Tab icon={<WorkIcon />} label="Professional" />
            <Tab icon={<WebsiteIcon />} label="Social Media" />
            <Tab icon={<FavoriteIcon />} label="Interests & Hobbies" />
          </Tabs>
        </Box>

        {/* Personal Information Tab */}
        <TabPanel value={currentTab} index={0}>
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              About Me
            </Typography>

            <TextField
              label="Display Name"
              fullWidth
              value={formData.chrDisplayName ?? ''}
              onChange={handleChange('chrDisplayName')}
              placeholder="Name shown next to @username"
              helperText={`Displayed as ${formData.chrDisplayName || profile.chrAliasName} (@${profile.chrAliasName})`}
            />
            
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Bio / About Me
            </Typography>
            <RichTextEditor
              value={formData.txtBio ?? ''}
              onChange={(html) => setFormData({ ...formData, txtBio: html })}
              placeholder="Tell people about yourself..."
              minHeight={240}
              maxBodyHeight="min(40vh, 360px)"
              uploadImageFile={uploadImageForEditor}
            />

            <Divider />

            {mediaUploadMessage && (
              <Alert severity={mediaUploadMessage.severity} onClose={() => setMediaUploadMessage(null)}>
                {mediaUploadMessage.text}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                <Typography variant="subtitle2" gutterBottom>Profile Picture</Typography>
                <ProfileMediaAvatar
                  mediaUrl={formData.chrProfileImageURL}
                  sx={{ width: 72, height: 72, mb: 1 }}
                />
                <Button variant="outlined" component="label" disabled={uploadingAvatar}>
                  {uploadingAvatar ? 'Uploading…' : 'Upload avatar'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleMediaUpload('avatar', file);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                <Typography variant="subtitle2" gutterBottom>Cover Photo</Typography>
                <ProfileCoverBanner
                  mediaUrl={formData.chrCoverImageURL}
                  minHeight={100}
                  sx={{ mb: 1, border: 1, borderColor: 'divider' }}
                />
                <Button variant="outlined" component="label" disabled={uploadingCover}>
                  {uploadingCover ? 'Uploading…' : 'Upload cover'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleMediaUpload('cover', file);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
            </Box>

            <TextField
              label="Location"
              fullWidth
              value={formData.chrLocation}
              onChange={handleChange('chrLocation')}
              placeholder="City, State/Country"
              helperText="Where are you based?"
            />

            <FormControl fullWidth>
              <InputLabel>Profile Visibility</InputLabel>
              <Select
                value={formData.intVisibilityLevel}
                onChange={(e) => setFormData({ ...formData, intVisibilityLevel: Number(e.target.value) })}
              >
                <MenuItem value={1}>🌍 Public - Anyone can see your profile</MenuItem>
                <MenuItem value={2}>👥 Friends Only - Only your connections</MenuItem>
                <MenuItem value={3}>🔒 Private - Only you</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </TabPanel>

        {/* Professional Information Tab */}
        <TabPanel value={currentTab} index={1}>
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Professional Life
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="body2">
                💼 <strong>Pro Tip:</strong> Add your work experience, skills, and professional website to attract opportunities!
              </Typography>
            </Paper>

            <TextField
              label="Current Job Title"
              fullWidth
              value={formData.chrJobTitle ?? ''}
              onChange={handleChange('chrJobTitle')}
              placeholder="e.g., Software Engineer, Content Creator, Entrepreneur"
              helperText="What do you do professionally?"
            />

            <TextField
              label="Company / Organization"
              fullWidth
              value={formData.chrCompany ?? ''}
              onChange={handleChange('chrCompany')}
              placeholder="Where do you work?"
            />

            <TextField
              label="Professional Website / Portfolio"
              fullWidth
              value={formData.chrWebsiteURL}
              onChange={handleChange('chrWebsiteURL')}
              placeholder="https://yourwebsite.com"
              helperText="Your personal website, portfolio, or company page"
            />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Skills &amp; Expertise
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder="Add a skill"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addSkill();
                    }
                  }}
                />
                <Button variant="outlined" onClick={addSkill} startIcon={<AddIcon />}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {skillsList.map((skill) => (
                  <Chip key={skill} label={skill} onDelete={() => removeSkill(skill)} />
                ))}
              </Box>
            </Box>

            <TextField
              label="Education"
              fullWidth
              value={formData.chrEducation ?? ''}
              onChange={handleChange('chrEducation')}
              placeholder="University, Degree, or Certifications"
            />

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Professional Bio
            </Typography>
            <RichTextEditor
              value={formData.txtProfessionalBio ?? ''}
              onChange={(html) => setFormData({ ...formData, txtProfessionalBio: html })}
              placeholder="Describe your professional journey..."
              minHeight={240}
              maxBodyHeight="min(40vh, 360px)"
              uploadImageFile={uploadImageForEditor}
            />
          </Stack>
        </TabPanel>

        {/* Social Media Tab */}
        <TabPanel value={currentTab} index={2}>
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Social Media & Links
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Connect your social media profiles so people can follow you across platforms
            </Typography>

            {/* Existing Social Links */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Your Connected Accounts
              </Typography>
              {profile.socialLinks.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography variant="body2" color="text.secondary">
                    No social links added yet. Add your first one below!
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1}>
                  {profile.socialLinks.map((link) => (
                    <Paper key={link.idSocialLinkID} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {getPlatformIcon(link.chrPlatform)}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2">{link.chrPlatform}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {link.chrPlatformUsername || link.chrPlatformURL}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteSocialLink(link.idSocialLinkID)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            {/* Add New Social Link */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Add New Social Link
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={newSocialLink.chrPlatform}
                    onChange={(e) => setNewSocialLink({ ...newSocialLink, chrPlatform: e.target.value })}
                  >
                    <MenuItem value="LinkedIn"><LinkedInIcon sx={{ mr: 1 }} /> LinkedIn</MenuItem>
                    <MenuItem value="Facebook"><FacebookIcon sx={{ mr: 1 }} /> Facebook</MenuItem>
                    <MenuItem value="Twitter"><TwitterIcon sx={{ mr: 1 }} /> Twitter / X</MenuItem>
                    <MenuItem value="Instagram"><InstagramIcon sx={{ mr: 1 }} /> Instagram</MenuItem>
                    <MenuItem value="YouTube"><YouTubeIcon sx={{ mr: 1 }} /> YouTube</MenuItem>
                    <MenuItem value="GitHub"><GitHubIcon sx={{ mr: 1 }} /> GitHub</MenuItem>
                    <MenuItem value="TikTok">TikTok</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Website"><WebsiteIcon sx={{ mr: 1 }} /> Other Website</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Username (optional)"
                  fullWidth
                  value={newSocialLink.chrPlatformUsername}
                  onChange={(e) => setNewSocialLink({ ...newSocialLink, chrPlatformUsername: e.target.value })}
                  placeholder="@username"
                />

                <TextField
                  label="Profile URL"
                  fullWidth
                  value={newSocialLink.chrPlatformURL}
                  onChange={(e) => setNewSocialLink({ ...newSocialLink, chrPlatformURL: e.target.value })}
                  placeholder="https://linkedin.com/in/yourprofile"
                  helperText="Full URL to your profile on this platform"
                />

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddSocialLink}
                  fullWidth
                >
                  Add Social Link
                </Button>
              </Stack>
            </Box>
          </Stack>
        </TabPanel>

        {/* Interests & Hobbies Tab */}
        <TabPanel value={currentTab} index={3}>
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Interests & Hobbies
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Share what you're passionate about! This helps others with similar interests find you.
            </Typography>

            {/* Existing Interests */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Your Interests ({interests.length})
              </Typography>
              {interests.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography variant="body2" color="text.secondary">
                    No interests added yet. Add some below to help people discover you!
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {interests.map((interest) => (
                    <Chip
                      key={interest.idInterestID}
                      label={interest.chrInterestTag}
                      color="primary"
                      variant="outlined"
                      onDelete={() => handleDeleteInterest(interest.idInterestID)}
                      icon={<FavoriteIcon />}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Divider />

            {/* Add New Interest */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Add New Interest
              </Typography>
              {interestError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setInterestError(null)}>
                  {interestError}
                </Alert>
              )}
              <Stack spacing={2}>
                <TextField
                  label="Interest / Hobby"
                  fullWidth
                  value={newInterest}
                  onChange={(e) => setNewInterest(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddInterest();
                    }
                  }}
                  placeholder="e.g., Blockchain, Photography, Hiking"
                />

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddInterest}
                  fullWidth
                >
                  Add Interest
                </Button>
              </Stack>
            </Box>

            {/* Popular Interest Suggestions */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Popular Interests (Click to add)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  'Blockchain', 'Cryptocurrency', 'Web3', 'DeFi', 'NFTs',
                  'Artificial Intelligence', 'Programming', 'Entrepreneurship',
                  'Content Creation', 'Photography', 'Travel', 'Fitness',
                  'Reading', 'Music', 'Gaming', 'Cooking', 'Yoga'
                ].map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => {
                      setNewInterest(tag);
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </TabPanel>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button variant="outlined" onClick={onCancel} size="large">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EnhancedProfileEditor;

